import { defineModule } from '../../engine/module';
import { MailboxWizard } from './MailboxWizard';
import { MailboxSetup } from './MailboxSetup';
import { MessageView } from './MessageView';
import { TemplateEditor } from './TemplateEditor';

// The email module's UI (docs/modules/email.md): every screen is an engine ScreenDef. The
// bespoke parts are a create-drawer component (the mailbox wizard) and detail tabs (mailbox
// setup, the sandboxed message viewer, the template editor) — never hand-written pages.

const MESSAGE_COLUMNS = ['direction', 'from_email', 'subject', 'classification', 'status', 'entity_type', 'received_at'];

export const emailModule = defineModule({
  name: 'email',
  nav: [
    {
      label: 'Email',
      order: 25,
      items: [
        { label: 'Messages', to: '/email/messages', icon: 'inbox' },
        { label: 'My signature', to: '/email/signature', icon: 'file-text' },
      ],
    },
    {
      label: 'Email',
      area: 'config',
      order: 60,
      items: [
        { label: 'Mailboxes', to: '/email/mailboxes', icon: 'mail', permission: 'mailboxes:list' },
        { label: 'Email templates', to: '/email/templates', icon: 'file-text' },
        { label: 'Exclusions', to: '/email/exclusions', icon: 'ban' },
      ],
    },
  ],
  screens: [
    {
      path: '/email/mailboxes',
      resource: 'mailboxes',
      kind: 'list',
      title: 'Mailboxes',
      subtitle: 'Inbound feeds and outbound senders — IMAP, SMTP, Microsoft 365, Gmail',
      columns: ['name', 'address', 'kind', 'status', 'inbound_enabled', 'outbound_enabled', 'last_polled_at'],
      filters: [
        { field: 'kind', kind: 'chips' },
        { field: 'status', kind: 'chips' },
      ],
      form: { component: MailboxWizard, mode: 'drawer' },
    },
    {
      path: '/email/mailboxes/:id',
      resource: 'mailboxes',
      kind: 'detail',
      tabs: [
        { key: 'setup', label: 'Setup', icon: 'plug-zap', component: MailboxSetup },
        { key: 'messages', label: 'Messages', icon: 'inbox', resource: 'email-messages', filter: { mailbox_id: ':id' }, columns: MESSAGE_COLUMNS },
      ],
      builtinTabs: ['overview', 'activity'],
    },
    {
      path: '/email/messages',
      resource: 'email-messages',
      kind: 'list',
      title: 'Messages',
      subtitle: 'Every email in and out, with how it was classified and where it landed',
      columns: MESSAGE_COLUMNS,
      filters: [
        { field: 'direction', kind: 'chips' },
        { field: 'classification', kind: 'chips' },
        { field: 'status', kind: 'chips' },
      ],
    },
    {
      path: '/email/messages/:id',
      resource: 'email-messages',
      kind: 'detail',
      tabs: [{ key: 'message', label: 'Message', icon: 'mail', component: MessageView }],
      // The message tab leads; the overview's field dump is second.
      builtinTabs: ['activity', 'attachments'],
    },
    {
      path: '/email/templates',
      resource: 'email-templates',
      kind: 'list',
      title: 'Email templates',
      subtitle: 'Seeded system templates you can edit and reset, plus your own',
      columns: ['key', 'name', 'subject', 'is_system', 'updated_at'],
      filters: [{ field: 'is_system', kind: 'toggle', label: 'System' }],
      form: { fields: ['key', 'name', 'description', 'subject', 'html', 'text'] },
    },
    {
      path: '/email/templates/:id',
      resource: 'email-templates',
      kind: 'detail',
      tabs: [{ key: 'editor', label: 'Editor', icon: 'code', component: TemplateEditor }],
      builtinTabs: ['activity'],
    },
    {
      path: '/email/exclusions',
      resource: 'email-exclusions',
      kind: 'list',
      title: 'Email exclusions',
      subtitle: 'Senders, domains and patterns that never create tickets',
      columns: ['kind', 'pattern', 'reason', 'is_active'],
      filters: [{ field: 'kind', kind: 'chips' }],
      inspector: true,
    },
    {
      path: '/email/signature',
      resource: 'email-signatures',
      kind: 'list',
      title: 'My signature',
      subtitle: 'Appended to the emails you send',
      columns: ['html', 'updated_at'],
      inspector: true,
    },
  ],
  commands: [
    { id: 'email-send', label: 'Send an email…', kind: 'create', icon: 'send', keywords: 'mail compose message', run: ({ navigate }) => navigate('/email/messages?send=1') },
    { id: 'email-mailbox', label: 'Add a mailbox…', kind: 'create', icon: 'mail', keywords: 'imap smtp graph gmail inbox', run: ({ navigate }) => navigate('/email/mailboxes?new=1') },
  ],
});
